import type { ConversationProgressDocument, ConversationTurn, FormField } from '../types';
import { turnKeyForTurn } from './transcript-timing';

/** An original prompt and any guest paragraphs that followed it. */
export interface ExploreExchange {
  key: string | number;
  prompt?: string;
  replies: string[];
}

/** A spoken guest turn can support several answers. Only a unique owner can anchor a follow-up. */
function evidenceOwners(progress: ConversationProgressDocument | null): Map<string, string | null> {
  const owners = new Map<string, string | null>();
  for (const step of progress?.steps ?? []) {
    const id = step.fieldId ?? step.stepId;
    if (!id) continue;
    for (const key of step.evidenceTurnIds) {
      owners.set(key, !owners.has(key) || owners.get(key) === id ? id : null);
    }
  }
  return owners;
}

/** Group typed exchanges by field ID and spoken follow-ups between two guest turns with the same owner. */
export function getExploreResponseExchanges(fields: FormField[] | null | undefined, turns: ConversationTurn[], progress: ConversationProgressDocument | null) {
  const result = new Map<string, ExploreExchange[]>();
  if (!Array.isArray(fields)) return result;
  const explore = new Map(fields.filter(field => field?.type === 'free_form_explore').map(field => [field.id, field]));
  if (!explore.size) return result;
  const evidence = evidenceOwners(progress);
  const dialogue = turns.filter(turn => turn.speaker === 'ai' || turn.speaker === 'user');
  /** Typed messages carry their own field ID. Only untagged guest evidence anchors spoken dialogue. */
  const spokenOwner = (turn?: ConversationTurn) => turn?.speaker === 'user' && !turn.fieldId && (turn.turn_id || turn.timestamp !== undefined)
    ? evidence.get(turnKeyForTurn(turn)) : undefined;
  let previousField: FormField | undefined;
  for (const [index, turn] of dialogue.entries()) {
    let owner = turn.fieldId ?? spokenOwner(turn);
    if (!owner && turn.speaker === 'ai' && !turn.interrupted) {
      const before = spokenOwner(dialogue[index - 1]);
      if (before && before === spokenOwner(dialogue[index + 1])) owner = before;
    }
    const field = owner ? explore.get(owner) : undefined;
    if (field && turn.text.trim()) {
      const exchanges = result.get(field.id) ?? [];
      let current = exchanges.at(-1);
      if (!current || previousField !== field || turn.speaker === 'ai') {
        current = { key: turn.turn_id ?? index, replies: [] };
        if (turn.speaker === 'ai' && (exchanges.length || turn.text !== field.label)) current.prompt = turn.text;
        exchanges.push(current);
        result.set(field.id, exchanges);
      }
      if (turn.speaker === 'user') current.replies.push(turn.text);
    }
    previousField = field;
  }
  return result;
}
